import type { OHLCVPoint, ProbabilityPoint } from './types';

/**
 * Generate realistic OHLCV price data using a random walk.
 * Used for the preview price chart until the real asset-price-history endpoint is available.
 */
export function generatePriceData(days = 120, basePrice = 62000): OHLCVPoint[] {
    const data: OHLCVPoint[] = [];
    let price = basePrice;

    for (let i = 0; i < days; i++) {
        const drift = Math.sin(i / 12) * 800 + Math.sin(i / 5) * 300 + 80;
        const noise = (Math.random() - 0.48) * price * 0.03;
        price = Math.max(price * 0.7, price + drift / days + noise);

        const open = price - Math.random() * price * 0.02;
        const close = price + (Math.random() - 0.5) * price * 0.015;
        const high = Math.max(open, close) + Math.random() * price * 0.025;
        const low = Math.min(open, close) - Math.random() * price * 0.03;

        data.push({
            t: new Date(2025, 0, 1 + i),
            open,
            high,
            low,
            close,
            vol: Math.random() * 5e9 + 2e9,
        });
    }
    return data;
}

/**
 * Generate a probability timeseries between two dates converging towards a final probability.
 * Used for preview probability charts until the real market-probability-history endpoint is available.
 */
export function generateProbSeries(
    startDate: Date,
    endDate: Date,
    finalProb = 0.65,
): ProbabilityPoint[] {
    const pts: ProbabilityPoint[] = [];
    const startMs = startDate.getTime();
    const endMs = endDate.getTime();
    const days = Math.max(1, Math.round((endMs - startMs) / (1000 * 60 * 60 * 24)));

    for (let i = 0; i <= days; i++) {
        const t = i / days;
        const base = 0.35 + t * (finalProb - 0.35);
        const prob = Math.max(0.05, Math.min(0.98, base + (Math.random() - 0.5) * 0.15));
        pts.push({
            t: new Date(startMs + i * 24 * 60 * 60 * 1000),
            prob,
        });
    }
    return pts;
}

/**
 * Generate probability series from index offsets (for Gantt chart overlay).
 */
export function generateProbSeriesFromIndices(
    start: number,
    end: number,
    finalProb: number,
): number[] {
    const pts: number[] = [];
    const len = end - start;
    for (let i = 0; i <= len; i++) {
        const t = i / len;
        const base = 0.35 + t * (finalProb - 0.35);
        pts.push(Math.max(0.05, Math.min(0.98, base + (Math.random() - 0.5) * 0.15)));
    }
    return pts;
}

// --- Mock Trades ---

export interface MockTrade {
    id: number;
    market: string;
    side: 'YES' | 'NO';
    price: number;
    size: number;
    time: string;
    wallet: string;
    large: boolean;
}

export const MOCK_TRADES: MockTrade[] = [
    { id: 1, market: 'BTC > $80k Dec 2025?', side: 'YES', price: 0.72, size: 4200, time: '12:04:31', wallet: '0x7a3...e1f2', large: true },
    { id: 2, market: 'Fed rate cut March?', side: 'NO', price: 0.69, size: 850, time: '12:04:28', wallet: '0xb2c...8d4a', large: false },
    { id: 3, market: 'BTC > $80k Dec 2025?', side: 'YES', price: 0.71, size: 12500, time: '12:04:22', wallet: '0x1f0...c3b7', large: true },
    { id: 4, market: 'Inflation > 3% Q2?', side: 'YES', price: 0.55, size: 320, time: '12:04:18', wallet: '0x9e1...47ab', large: false },
    { id: 5, market: 'BTC ETF inflows > $5B Q1?', side: 'NO', price: 0.36, size: 1600, time: '12:04:12', wallet: '0x3d8...f901', large: false },
    { id: 6, market: 'BTC > $80k Dec 2025?', side: 'YES', price: 0.72, size: 8900, time: '12:04:05', wallet: '0x7a3...e1f2', large: true },
    { id: 7, market: 'SEC enforcement before June?', side: 'NO', price: 0.59, size: 2100, time: '12:03:58', wallet: '0xc44...2e1d', large: false },
    { id: 8, market: 'Fed rate cut March?', side: 'YES', price: 0.31, size: 500, time: '12:03:51', wallet: '0xa7f...b830', large: false },
    { id: 9, market: 'BTC > $80k Dec 2025?', side: 'NO', price: 0.28, size: 3400, time: '12:03:44', wallet: '0x5b2...d9e3', large: false },
    { id: 10, market: 'Inflation > 3% Q2?', side: 'YES', price: 0.56, size: 18200, time: '12:03:38', wallet: '0x1f0...c3b7', large: true },
    { id: 11, market: 'BTC ETF inflows > $5B Q1?', side: 'YES', price: 0.64, size: 720, time: '12:03:30', wallet: '0xe22...a4c1', large: false },
    { id: 12, market: 'BTC > $80k Dec 2025?', side: 'YES', price: 0.71, size: 6100, time: '12:03:22', wallet: '0x9e1...47ab', large: true },
    { id: 13, market: 'SEC enforcement before June?', side: 'YES', price: 0.41, size: 950, time: '12:03:14', wallet: '0xb2c...8d4a', large: false },
    { id: 14, market: 'Fed rate cut March?', side: 'NO', price: 0.70, size: 2800, time: '12:03:05', wallet: '0x3d8...f901', large: false },
    { id: 15, market: 'BTC > $80k Dec 2025?', side: 'YES', price: 0.72, size: 31000, time: '12:02:58', wallet: '0x7a3...e1f2', large: true },
];

// --- Mock News ---

export interface MockNewsItem {
    id: number;
    source: string;
    time: string;
    title: string;
    snippet: string;
    tag: string;
    hot: boolean;
}

export const MOCK_NEWS: MockNewsItem[] = [
    { id: 1, source: 'CoinDesk', time: '12 min ago', title: 'Bitcoin Surges Past $71k as Institutional Demand Accelerates', snippet: 'Major asset managers increase BTC allocations amid growing ETF inflows and favorable macro conditions.', tag: 'bullish', hot: true },
    { id: 2, source: 'Reuters', time: '34 min ago', title: 'Fed Officials Signal Patience on Rate Cuts Despite Cooling Inflation', snippet: 'Several FOMC members emphasized the need for more data before adjusting monetary policy stance.', tag: 'macro', hot: false },
    { id: 3, source: 'Bloomberg', time: '1h ago', title: "BlackRock's Bitcoin ETF Sees Record $620M Single-Day Inflow", snippet: 'IBIT continues to dominate spot Bitcoin ETF market with cumulative net inflows exceeding $18 billion.', tag: 'bullish', hot: true },
    { id: 4, source: 'The Block', time: '2h ago', title: 'Polymarket Volume Hits All-Time High on Crypto-Related Markets', snippet: 'Prediction market trading surges as users bet on Bitcoin price targets and regulatory outcomes.', tag: 'markets', hot: false },
    { id: 5, source: 'WSJ', time: '3h ago', title: 'SEC Chair Signals Openness to Revised Crypto Regulatory Framework', snippet: 'In congressional testimony, the chair outlined potential pathways for clearer digital asset rules.', tag: 'regulation', hot: false },
    { id: 6, source: 'CoinTelegraph', time: '4h ago', title: 'Bitcoin Mining Difficulty Reaches New All-Time High', snippet: 'Network hash rate continues climbing post-halving as miners invest in next-gen hardware.', tag: 'neutral', hot: false },
    { id: 7, source: 'CNBC', time: '5h ago', title: 'MicroStrategy Adds Another 3,000 BTC to Treasury Holdings', snippet: 'The company now holds over 210,000 Bitcoin, worth approximately $15 billion at current prices.', tag: 'bullish', hot: true },
    { id: 8, source: 'Financial Times', time: '6h ago', title: 'US Dollar Weakens as Markets Price In Rate Cut Expectations', snippet: 'The DXY index fell to a three-month low, providing tailwinds for risk assets including crypto.', tag: 'macro', hot: false },
];


import { NormalizedAssetData } from './asset-tab-types';

export const MOCK_BTC_DATA: NormalizedAssetData = {
    issuer: {
        name: null,
        type: "decentralized",
        governance: "Decentralized consensus-based governance via network participants and miners",
        key_figures: [
            "Satoshi Nakamoto (pseudonymous creator)"
        ]
    },
    description: {
        short_summary: "Bitcoin is the world's first decentralized cryptocurrency created in 2009 by pseudonymous Satoshi Nakamoto. It enables peer-to-peer electronic cash transactions without intermediaries, secured by Proof of Work mining. With a fixed supply cap of 21 million coins and programmatic halvings every four years, Bitcoin is designed as a deflationary digital asset.",
        primary_use_cases: [
            "Store of value",
            "Digital currency for peer-to-peer transactions",
            "Treasury reserve asset",
            "Collateral for financial applications",
            "Medium of exchange"
        ],
        key_characteristics: [
            "Decentralized peer-to-peer network",
            "Fixed supply cap of 21 million",
            "Proof of Work consensus mechanism",
            "SHA-256 cryptographic algorithm",
            "10-minute block time",
            "Programmable halvings every 210,000 blocks",
            "Censorship-resistant and permissionless",
            "Blockchain-based public ledger"
        ]
    },
    identifiers: {
        name: "Bitcoin",
        primary_symbol: "BTC",
        alternative_names: [
            "digital gold",
            "digital currency"
        ],
        alternative_symbols: [
            "XBT"
        ]
    },
    asset_specific: {
        index: null,
        company: null,
        commodity: null,
        technology: {
            max_supply: 21000000,
            key_features: [
                "UTXO model",
                "Decentralized consensus",
                "Programmable halvings",
                "SegWit upgrade support",
                "Taproot activation",
                "Ordinals protocol support (NFTs)",
                "BRC-20 token standard (experimental)",
                "BTCFi applications enabled"
            ],
            platform_type: "Layer 1 blockchain",
            hashing_algorithm: "SHA-256",
            block_time_minutes: 10,
            consensus_mechanism: "Proof of Work (PoW)",
            mining_reward_current: 3.125,
            supply_mined_percentage: 93.74,
            current_circulating_supply_approx: 19686975
        },
        currency_pair: null
    },
    classification: {
        sector: null,
        asset_type: "cryptocurrency",
        subcategories: [
            "Proof of Work (PoW)",
            "Bitcoin Ecosystem"
        ],
        primary_category: "Layer 1 (L1)"
    },
    market_drivers: {
        macro_sensitivities: [
            {
                strength: "high",
                indicator: "Interest Rates",
                mechanism: "Rising rates increase opportunity cost of holding non-yielding assets like Bitcoin; falling rates favor risk assets",
                relationship: "negative",
                indicator_type: "monetary_policy",
                alternative_names: ["federal funds rate", "monetary tightening/easing", "real interest rates"]
            },
            {
                strength: "high",
                indicator: "Inflation (CPI)",
                mechanism: "Bitcoin marketed as inflation hedge and store of value; rising inflation drives demand for assets outside fiat currency system",
                relationship: "positive",
                indicator_type: "macroeconomic",
                alternative_names: ["Consumer Price Index", "price levels", "currency debasement"]
            },
            {
                strength: "medium",
                indicator: "GDP Growth",
                mechanism: "Economic expansion supports risk asset demand; recession reduces speculative capital and institutional interest",
                relationship: "non-linear",
                indicator_type: "macroeconomic",
                alternative_names: ["economic growth", "business cycle", "aggregate demand"]
            },
            {
                strength: "high",
                indicator: "US Dollar Strength",
                mechanism: "Stronger dollar reduces Bitcoin appeal as alternative store of value and increases carrying costs for international investors",
                relationship: "negative",
                indicator_type: "currency_markets",
                alternative_names: ["DXY index", "currency valuation", "dollar appreciation"]
            },
            {
                strength: "high",
                indicator: "Financial Regulation/Policy Clarity",
                mechanism: "Clear regulatory frameworks reduce uncertainty and enable institutional adoption; enforcement actions create headwinds",
                relationship: "positive",
                indicator_type: "regulatory",
                alternative_names: ["regulatory environment", "legal clarity", "policy framework"]
            },
            {
                strength: "medium",
                indicator: "Stock Market Performance",
                mechanism: "Risk appetite correlation; equities falling typically dragging Bitcoin down; equity rallies supporting Bitcoin strength",
                relationship: "positive",
                indicator_type: "asset_markets",
                alternative_names: ["equity market sentiment", "stock indices", "risk appetite"]
            },
            {
                strength: "medium",
                indicator: "Credit Market Conditions",
                mechanism: "Tight credit conditions reduce liquidity and margin availability affecting Bitcoin trading volumes and leverage positions",
                relationship: "positive",
                indicator_type: "financial_markets",
                alternative_names: ["credit spreads", "liquidity conditions", "risk premium"]
            },
            {
                strength: "medium",
                indicator: "Geopolitical Risk/Sanctions",
                mechanism: "Capital controls and sanctions drive demand for permissionless digital assets; Bitcoin serves as censorship-resistant store of value",
                relationship: "positive",
                indicator_type: "geopolitical",
                alternative_names: ["geopolitical tension", "capital flight", "financial coercion risk"]
            }
        ],
        sector_dependencies: [
            {
                sector: "Financial Services",
                strength: "high",
                relationship: "Growing institutional adoption through ETFs and custody services creating dependency on banking infrastructure and regulatory approvals"
            },
            {
                sector: "Technology/Computing",
                strength: "high",
                relationship: "Mining industry dependent on semiconductor manufacturing and electricity markets; network security depends on computing power"
            },
            {
                sector: "Energy",
                strength: "high",
                relationship: "Proof of Work mining requires significant electricity; Bitcoin sensitive to energy prices and availability"
            }
        ],
        geopolitical_factors: [
            {
                factor: "US-China Technology Competition",
                impact: "Chinese mining restrictions and export controls on semiconductors affect global Bitcoin mining capacity and distribution",
                strength: "medium"
            },
            {
                factor: "Government Bitcoin Holdings",
                impact: "Government accumulation (Texas, other states) and El Salvador adoption signal legitimacy but also increase regulatory scrutiny",
                strength: "medium"
            },
            {
                factor: "Russia Sanctions and Evasion",
                impact: "Bitcoin demand increases during international sanctions as governments seek capital flight mechanisms; regulatory crackdowns follow",
                strength: "medium"
            }
        ]
    },
    search_keywords: [
        "Bitcoin price", "BTC prediction market", "halving 2028", "spot Bitcoin ETF",
        "Federal Reserve interest rates", "Bitcoin regulation US", "El Salvador Bitcoin legal tender",
        "Bitcoin mining difficulty", "GENIUS Act stablecoin", "institutional Bitcoin adoption",
        "Bitcoin dominance", "market cap rank #1", "digital gold", "store of value",
        "Bitcoin ETF flows", "MicroStrategy Bitcoin treasury", "Bitcoin vs inflation",
        "Proof of Work mining", "blockchain security", "21 million supply cap"
    ],
    market_structure: {
        related_assets: {
            competitors: ["Ethereum", "Solana", "Cardano", "BNB", "Ripple (XRP)", "Dogecoin", "Litecoin"],
            complements: [
                "Bitcoin ETFs (iShares Bitcoin Trust, Grayscale Bitcoin Trust)",
                "Lightning Network",
                "Stablecoins backed by Bitcoin",
                "Bitcoin futures contracts"
            ],
            derivatives: ["Bitcoin futures", "Bitcoin options", "Bitcoin ETFs", "Bitcoin ETN (Exchange Traded Notes)"]
        },
        market_position: "Market-leading cryptocurrency with highest market cap and dominance at 56.8-58.65% of total crypto market as of February 2026",
        primary_markets: [
            "Global cryptocurrency exchanges (Coinbase, Kraken, Binance, etc.)",
            "Traditional finance platforms offering spot Bitcoin ETFs",
            "Over-the-counter (OTC) markets",
            "Derivatives exchanges (CME, NASDAQ)"
        ],
        regulatory_status: "SEC approved spot Bitcoin ETFs in 2024 with significant institutional growth by late September 2025 with $150-170 billion in assets. In 2026, the US, EU, and Asian countries strengthened cryptocurrency regulation, particularly anti-money laundering enforcement. El Salvador recognized Bitcoin as legal tender in 2021. Regulatory framework still evolving with GENIUS Act and market structure legislation pending implementation."
    },
    events_and_catalysts: {
        recurring_events: [
            {
                type: "Bitcoin Halving",
                impact: "Reduces mining rewards by 50%, constrains new supply, historically associated with price increases and market volatility in following months",
                frequency: "Every 210,000 blocks (~4 years)",
                next_date: "April 17, 2028"
            },
            {
                type: "FOMC Meetings",
                impact: "Interest rate decisions influence Bitcoin valuations through macroeconomic effects on risk assets and dollar strength",
                frequency: "8 times per year",
                next_date: "As scheduled by Federal Reserve"
            },
        ],
        scheduled_events: [
            "California Digital Financial Assets Law takes effect July 1, 2026, requiring crypto companies to obtain licenses",
            "US Senate expected to restart hearings on CLARITY Act market structure legislation in January 2026",
            "Federal Reserve Chair Jerome Powell's term ends May 2026; successor may be more open to interest rate cuts",
            "US midterm elections November 2026 could reshape Congress and determine crypto legislation progress"
        ],
        historical_events: [
            "Genesis block created January 3, 2009",
            "First Bitcoin transaction January 12, 2009",
            "First Bitcoin halving November 28, 2012",
            "Second halving July 9, 2016",
            "Third halving May 11, 2020",
            "Fourth halving April 20, 2024",
            "January 2024 SEC approval of 11 spot Bitcoin ETFs",
            "July 2025 GENIUS Act signed into law",
            "Emergence of Ordinals enabling NFTs on Bitcoin (January 2023)",
            "Strategy Inc. (formerly MicroStrategy) adoption as treasury reserve asset",
            "Babylon protocol enabling Bitcoin staking to Proof of Stake chains"
        ]
    }
};